import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";
import { Moment } from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useStressEntryContext } from "./StressEntryContext";
import { STRESSOR_CHECK_IN_MODE, STRESSOR_CHECK_OUT_MODE } from "./constants";
import { stat } from "fs";
import { Add as AddIcon } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useSmlFile } from "../../../common/services/fileServices";
import { toast } from "react-toastify";
import { useFetchContext } from "../../../common/FetchContext";
import { useSubmitSmlFileUpload } from "../../../testCommunication/common/testMutations";
import { DropzoneArea } from "material-ui-dropzone";

interface TableRowData {
  col1: string | null;
  col2a: boolean;
  col2b: boolean;
  col3: number | null;
  col4: number | null;
  col5: string | null;
  col6: number | null;
  col7: number | null;
  col8: string | null;

  col9: number | null;
  col10: number | null;
}

const initialRow: TableRowData = {
  col1: "",
  col2a: false,
  col2b: false,
  col3: null,
  col4: null,
  col5: "",
  col6: null,
  col7: null,
  col8: "",
  col9: null,
  col10: null,
};

export const SMLFields = ({ UnitVersion }: any) => {
  const [v12TestPressure, setV12TestPressure] = useState<(number | null)[]>(
    Array.from({ length: 6 }, (_, i) => 1800 + i * 100),
  );

  const [originalPressure, setOriginalPressure] = useState<(number | null)[]>(
    Array(6).fill(null),
  );
  const [rows, setRows] = useState<TableRowData[]>(
    Array(6)
      .fill(null)
      .map(() => ({ ...initialRow })),
  );
  const { mutateAsync: mutateSubmitSmlFileUpload } = useSubmitSmlFileUpload();
  const { authAxios } = useFetchContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [openPreview, setOpenPreview] = useState(false);

  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const [PreviewData, setPreviewData] = useState<any[]>([]);
  const [files, setFiles] = React.useState<any[]>([]);

  const { mutateAsync: uploadSmlFile, isLoading } = useSmlFile();

  const handlePreview = () => {
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      const file = event.target.files?.[0];

      if (!file) return;

      const response = await uploadSmlFile({
        authAxios,
        file,
      });

      const preview = await mutateSubmitSmlFileUpload({
        authAxios,
        asset_id: state.selectedStressType.id,
        azure_file_id: response.id,
        type: "preview",
        procedure_result_id: state?.procedureResultIds?.[0]?.procedureResultId,
      });
      setPreviewData(preview?.results);

      dispatch({
        type: "SET_SML_FILE_ID",
        payload: {
          Sml_file_id: response.id,
        },
      });

      setUploadedData(response.results);
      toast.success("File uploaded Successfully");
    } catch (error) {
      console.error("Upload Failed", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload the file";

      toast.error(errorMessage);
    }
  };

  const handleFileChange = (uploadedFiles: File[]) => {
    setFiles(uploadedFiles);

    dispatch({
      type: "SET_SML_IMAGES",
      payload: {
        Sml_images: uploadedFiles,
      },
    });
  };
  const handleTableChange = <K extends keyof TableRowData>(
    index: number,
    field: K,
    value: TableRowData[K],
  ) => {
    const updatedRows = [...rows];
    updatedRows[index] = {
      ...updatedRows[index],
      [field]: value,
    };
    setRows(updatedRows);
  };

  const handleV12TestPressureChange = (index: number, value: number | null) => {
    // Only first row is editable
    if (index !== 0) return;

    if (value === null) {
      // Clear all rows
      setV12TestPressure(Array(rows.length).fill(null));
      return;
    }

    // Generate sequence
    setV12TestPressure(
      Array.from({ length: rows.length }, (_, i) => value + i * 100),
    );
  };

  const [formData, setFormData] = useState({
    Ambient_temperature: "",
    Ambient_humidity: "",
    Calibration_date: null as Moment | null,
    Installation_method: "",
    Mounting_hardware: "",
  });

  const theme = useTheme();
  const installationOptions = [
    "Corner clamps",
    "Tracker 400mm holes",
    "Tracker 790mm holes",
    "Tracker 400mm clamps",
    "Fixed rack ¼ clamps",
  ];
  const { state, dispatch } = useStressEntryContext();
  const isV12SMLTTF =
    UnitVersion?.version === "v12" &&
    state.procedureResultIds?.some(
      (p: any) => p.procedureDefinitionName === "SML-TTF",
    );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Moment | null) => {
    setFormData((prev) => ({ ...prev, Calibration_date: date }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const statKeys =
    PreviewData.length > 0 ? Object.keys(PreviewData[0].stats || {}) : [];

  // const addRow = () => {
  //     if (rows.length < 11) {
  //         setRows([...rows, { ...initialRow }]);
  //     }
  // };

  const addRow = () => {
    if (rows.length < 11) {
      const newRows = [...rows, { ...initialRow }];
      setRows(newRows);

      const firstValue = v12TestPressure[0];

      if (firstValue == null) {
        setV12TestPressure(Array(newRows.length).fill(null));
      } else {
        setV12TestPressure(
          Array.from(
            { length: newRows.length },
            (_, i) => firstValue + i * 100,
          ),
        );
      }
    }
  };

  const buildPayload = () => {
    const entries = rows.map((row) => {
      let test_surface = "";
      if (row.col2a) test_surface += "front";
      if (row.col2b) test_surface += (test_surface ? "_" : "") + "back";

      return {
        test_pressure: row.col3,
        test_direction: row.col8,
        test_duration: row.col4,
        dut_circuit_normal: row.col5,
        dut_deflection: row.col6,
        load_uniformity: row.col7,
        test_surface: test_surface,
      };
    });

    return { entries };
  };

  useEffect(() => {
    const payload = {
      entries: rows.map((row, index) => {
        let test_surface = "";
        if (row.col2a && row.col2b) test_surface = "front_back";
        else if (row.col2a) test_surface = "front";
        else if (row.col2b) test_surface = "back";

        return {
          test_pressure: isV12SMLTTF ? v12TestPressure[index] : row.col3,

          test_direction: row.col5 ? row.col5.trim().toUpperCase() : null,
          test_duration: row.col4 || null,
          dut_circuit_normal: !isV12SMLTTF ? true : null,
          dut_deflection: row.col6 || null,
          load_uniformity: row.col7 || null,
          dut_defl_short_edge1: row.col9 || null,
          dut_defl_short_edge2: row.col10 || null,
          test_surface,
        };
      }),
    };
    dispatch({ type: "SMLTEST_STRESS_EXIT_DETAILS", payload });
  }, [rows, dispatch]);

  useEffect(() => {
    const payload = {
      Calibration_date: formData.Calibration_date
        ? formData.Calibration_date.format("YYYY-MM-DD")
        : null,
      Ambient_temperature: formData.Ambient_temperature
        ? parseFloat(formData.Ambient_temperature)
        : null,
      Ambient_humidity: formData.Ambient_temperature
        ? parseFloat(formData.Ambient_humidity)
        : null,
      Installation_method: formData.Installation_method,
      Mounting_hardware: formData.Mounting_hardware,
    };

    dispatch({ type: "SML_STRESS_DETAILS", payload });
  }, [formData, dispatch]);

  return (
    <div>
      {state.mode === STRESSOR_CHECK_IN_MODE && state.isSMLTestCheck && (
        <Grid
          container
          direction="row"
          justifyContent="space-around"
          alignItems="flex-start"
          spacing={2}
          style={{ marginTop: 16, marginBottom: 16 }}
        >
          <Grid item xs={10}>
            <Grid container spacing={2}>
              <Paper
                style={{
                  padding: "50px",
                  marginTop: 16,
                  marginBottom: 16,
                }}
              >
                <Grid item xs={12}>
                  <TextField
                    name="Ambient_temperature"
                    label="Ambient temperature"
                    type="number"
                    value={formData.Ambient_temperature}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">°C</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Ambient_humidity"
                    label="Ambient humidity"
                    type="number"
                    value={formData.Ambient_humidity}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%RH</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DateTimePicker
                    label="Calibration Date"
                    format="YYYY-MM-DD HH:mm"
                    fullWidth
                    value={formData.Calibration_date}
                    onChange={handleDateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    freeSolo
                    options={installationOptions}
                    value={formData.Installation_method}
                    onChange={(event, newValue) => {
                      handleChange({
                        target: {
                          name: "Installation_method",
                          value: newValue || "",
                        },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    onInputChange={(event, newInputValue) => {
                      handleChange({
                        target: {
                          name: "Installation_method",
                          value: newInputValue,
                        },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        name="Installation_method"
                        label="Installation method"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Mounting_hardware"
                    label="Mounting hardware"
                    value={formData.Mounting_hardware}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      )}

      {state.mode === STRESSOR_CHECK_OUT_MODE && state.isSMLTestCheck && (
        <>
          {isV12SMLTTF ? (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper elevation={3} style={{ padding: 16 }}>
                    <Typography
                      variant="h5"
                      color="primary"
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      {isLoading ? (
                        <>
                          Upload SML Test Report
                          <CircularProgress size={24} />
                        </>
                      ) : PreviewData.length > 0 ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handlePreview}
                        >
                          Preview SML Data
                        </Button>
                      ) : (
                        <>
                          Upload SML Test Report
                          <img
                            src="/file_upload.svg"
                            alt="Upload"
                            width="30"
                            height="30"
                            onClick={handleFileUploadClick}
                            style={{ cursor: "pointer" }}
                          />
                        </>
                      )}
                    </Typography>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      style={{ display: "none" }}
                      onChange={handleFileUpload}
                    />

                    <Grid container spacing={2} style={{ marginTop: 16 }}>
                      <Grid item xs={12}>
                        <Typography variant="h5" color="primary">
                          SML Image Upload
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <DropzoneArea
                          acceptedFiles={["image/*"]}
                          onChange={(files) => handleFileChange(files)}
                          filesLimit={
                            process.env.REACT_APP_FILE_LIMIT
                              ? parseInt(process.env.REACT_APP_FILE_LIMIT)
                              : 20
                          }
                          maxFileSize={12000000}
                          data-testid="fileUploadCrate"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
              <Dialog
                open={openPreview}
                onClose={handleClosePreview}
                maxWidth="lg"
                fullWidth
              >
                <DialogTitle>Preview SML Test Report</DialogTitle>

                <DialogContent>
                  <TableContainer
                    component={Paper}
                    style={{
                      maxHeight: 500,
                      overflowY: "auto",
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b></b>
                          </TableCell>

                          {PreviewData.map((result: any, index: number) => (
                            <TableCell key={index} align="center">
                              <b>{result.pressure} Pa</b>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {statKeys.map((statKey: string) => (
                          <TableRow key={statKey}>
                            <TableCell>
                              <b>{statKey}</b>
                            </TableCell>

                            {PreviewData.map((result: any, index: number) => (
                              <TableCell key={index} align="center">
                                {result.stats?.[statKey] ?? "-"}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DialogContent>

                <DialogActions>
                  <Button onClick={handleClosePreview}>ok</Button>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Test Direction</TableCell>
                    <TableCell align="center">Test pressure(Pa)</TableCell>
                    <TableCell align="center">Time duration(hours)</TableCell>
                    <TableCell align="center">
                      Current and voltage are normal
                    </TableCell>
                    <TableCell align="center">Deflection on (mm)</TableCell>
                    <TableCell align="center">Uniformity(%)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell
                        style={{
                          minWidth: 150,
                          maxWidth: 150,
                          width: 150,
                          alignContent: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Checkbox
                            checked={row.col2a}
                            onChange={(e) =>
                              handleTableChange(
                                index,
                                "col2a",
                                e.target.checked,
                              )
                            }
                          />
                          <span>Front</span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Checkbox
                            checked={row.col2b}
                            onChange={(e) =>
                              handleTableChange(
                                index,
                                "col2b",
                                e.target.checked,
                              )
                            }
                          />
                          <span>Back</span>
                        </div>
                      </TableCell>
                      <TableCell
                        style={{ minWidth: 100, maxWidth: 100, width: 100 }}
                      >
                        <TextField
                          type="number"
                          value={row.col3}
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "col3",
                              parseFloat(e.target.value),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell
                        style={{ minWidth: 100, maxWidth: 100, width: 100 }}
                      >
                        <TextField
                          type="number"
                          value={row.col4}
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "col4",
                              parseFloat(e.target.value),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell
                        style={{ minWidth: 200, maxWidth: 200, width: 200 }}
                      >
                        <TextField
                          fullWidth
                          value={row.col5}
                          onChange={(e) =>
                            handleTableChange(index, "col5", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell
                        style={{ minWidth: 100, maxWidth: 100, width: 100 }}
                      >
                        <TextField
                          type="number"
                          value={row.col6}
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "col6",
                              parseFloat(e.target.value),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell
                        style={{ minWidth: 100, maxWidth: 100, width: 100 }}
                      >
                        <TextField
                          type="number"
                          value={row.col7}
                          onChange={(e) =>
                            handleTableChange(
                              index,
                              "col7",
                              parseFloat(e.target.value),
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </div>
  );
};
